import * as React from "react";

import type {
  BillingMode,
  BillingStatus,
  InvoiceStatus,
  PaymentStatus,
  ScheduleStatus,
} from "api/billing";
import { Badge, type BadgeProps } from "./Badge";

export type StatusTone = NonNullable<BadgeProps["tone"]>;

export interface StatusMeta {
  tone: StatusTone;
  label: string;
  /** Shown as the badge's title (hover text). */
  description?: string;
}

/** Tenant.lifecycleStatus enum in the backend's models/Tenant.js. */
export type TenantLifecycleStatus = "active" | "disabled" | "not_working";

const TENANT: Record<TenantLifecycleStatus, StatusMeta> = {
  active: { tone: "success", label: "Active" },
  disabled: { tone: "neutral", label: "Disabled", description: "Turned off by an admin." },
  not_working: { tone: "danger", label: "Not working" },
};

// "overdue" is not stored on the invoice: pages derive it (open invoice past its due date) and
// show it next to the stored status.
const INVOICE: Record<InvoiceStatus | "overdue", StatusMeta> = {
  draft: { tone: "neutral", label: "Draft" },
  issued: { tone: "info", label: "Issued" },
  partially_paid: { tone: "warning", label: "Partly paid" },
  paid: { tone: "success", label: "Paid" },
  void: { tone: "neutral", label: "Void" },
  overdue: { tone: "danger", label: "Overdue" },
};

// Derived per client by services/billing/stateService.js.
const BILLING: Record<BillingStatus, StatusMeta> = {
  read_only: {
    tone: "danger",
    label: "Read-only",
    description: "TeleOpd is read-only for staff until the overdue amount is paid.",
  },
  overdue: {
    tone: "danger",
    label: "Overdue",
    description: "At least one invoice is past its due date.",
  },
  due_soon: {
    tone: "warning",
    label: "Due soon",
    description: "An invoice is inside its warning window.",
  },
  paused: { tone: "info", label: "Paused", description: "Enforcement is paused by an admin." },
  paid_up: { tone: "success", label: "Paid up", description: "Nothing is due right now." },
  no_billing: { tone: "neutral", label: "No billing", description: "No charges or invoices yet." },
};

// What TeleOpd shows its staff (the `mode` in the signed sync snapshot).
const BILLING_MODE: Record<BillingMode, StatusMeta> = {
  ok: { tone: "success", label: "Normal", description: "TeleOpd shows no billing notice." },
  warning: {
    tone: "warning",
    label: "Warning",
    description: "TeleOpd shows the payment warning to the chosen roles.",
  },
  read_only: { tone: "danger", label: "Read-only", description: "TeleOpd blocks staff writes." },
};

const SCHEDULE: Record<ScheduleStatus, StatusMeta> = {
  active: { tone: "success", label: "Active" },
  paused: { tone: "warning", label: "Paused" },
  ended: { tone: "neutral", label: "Ended" },
};

/** Tenant.stage: rollout stage while SAP is in testing (backend services/tenantStage.js). */
export type TenantStage = "beta" | "locked";

const STAGE: Record<TenantStage, StatusMeta> = {
  beta: {
    tone: "primary",
    label: "Beta",
    description: "Actionable: can be changed in SAP while it is in testing.",
  },
  locked: {
    tone: "neutral",
    label: "Locked",
    description: "Read-only in SAP until the production release.",
  },
};

const PAYMENT: Record<PaymentStatus, StatusMeta> = {
  succeeded: { tone: "success", label: "Received" },
  reversed: { tone: "neutral", label: "Reversed" },
};

/**
 * The one place a status gets its color and label, so "Overdue" looks the same on every page.
 * Add new values here rather than styling a Badge inline.
 */
export const STATUS_TONES = {
  tenant: TENANT,
  invoice: INVOICE,
  billing: BILLING,
  billingMode: BILLING_MODE,
  schedule: SCHEDULE,
  payment: PAYMENT,
  stage: STAGE,
};

export type StatusDomain = keyof typeof STATUS_TONES;

/** "not_working" → "Not Working", for values the map does not know yet. */
const titleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

/** Tone and label for a status; unknown values come back neutral with a readable label. */
export const getStatusMeta = (
  domain: StatusDomain,
  status: string | null | undefined
): StatusMeta => {
  const map: Partial<Record<string, StatusMeta>> = STATUS_TONES[domain];
  if (status && Object.prototype.hasOwnProperty.call(map, status)) {
    const meta = map[status];
    if (meta) {
      return meta;
    }
  }
  return { tone: "neutral", label: status ? titleCase(status) : "Unknown" };
};

export interface StatusBadgeProps extends Omit<BadgeProps, "tone" | "children"> {
  domain: StatusDomain;
  status: string | null | undefined;
}

/** Badge with a status dot, colored from STATUS_TONES. */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  domain,
  status,
  dot = true,
  title,
  ...props
}) => {
  const meta = getStatusMeta(domain, status);
  return (
    <Badge
      data-ui="status-badge"
      data-status={status || undefined}
      tone={meta.tone}
      dot={dot}
      title={title ?? meta.description}
      {...props}
    >
      {meta.label}
    </Badge>
  );
};
